import React, {useEffect, useMemo, useRef} from "react";

import {useFrame} from "@react-three/fiber";
import {
  AugmentedEvent,
  Player,
  ShapeType,
  SimulationEventType,
} from "@repo/shared";
import {Matrix4, Vector3} from "three";

import {useGame} from "~/contexts/game-manager/GameManagerProvider";
import {useGameState} from "~/contexts/gamestate/useGameState";
import {useServerTime} from "~/contexts/time/ServerTimeProvider";

const useReusableThreeObjects = () => {
  return useMemo(
    () => ({
      playerPosVec: new Vector3(),
      shardOriginalPos: new Vector3(),

      scaleMatrix: new Matrix4(),
      translateBackToShard: new Matrix4(),
      translateToPlayer: new Matrix4(),
      finalTransform: new Matrix4(),

      hideMatrix: new Matrix4().makeScale(0, 0, 0),
    }),
    [],
  );
};

interface AnimatingShard {
  id: number;
  collectedAt: number;
  player: Player;
}

const ANIMATION_DURATION_MS = 600;

const ShardCollectedAnimation: React.FC = () => {
  const {gameManager} = useGame();
  const {getFullInstanceTypes} = useGameState();
  const getServerTime = useServerTime();

  const animatingShardsRef = useRef(new Map<number, AnimatingShard>());

  const {
    playerPosVec,
    shardOriginalPos,
    scaleMatrix,
    translateBackToShard,
    translateToPlayer,
    finalTransform,
    hideMatrix,
  } = useReusableThreeObjects();

  // ---------------------------------------------------------------------------
  // EVENTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!gameManager) return;

    const handleSimulationEvent = (event: AugmentedEvent) => {
      if (
        event.isPrimary &&
        event.type === SimulationEventType.PlayerHitShard
      ) {
        animatingShardsRef.current.set(event.shardId!, {
          id: event.shardId!,
          collectedAt: getServerTime(),
          player: event.player,
        });
      }
    };

    const handleRoundStarted = () => {
      // IMPORTANT: cancel all in-flight animations
      animatingShardsRef.current.clear();
    };

    gameManager.on("simulation-event", handleSimulationEvent);
    gameManager.on("round-started", handleRoundStarted);

    return () => {
      animatingShardsRef.current.clear();

      gameManager.off("simulation-event", handleSimulationEvent);
      gameManager.off("round-started", handleRoundStarted);
    };
  }, [gameManager, getServerTime]);

  // ---------------------------------------------------------------------------
  // ANIMATION LOOP
  // ---------------------------------------------------------------------------
  useFrame(() => {
    if (!gameManager || animatingShardsRef.current.size === 0) {
      return;
    }

    const circleInstanceData = getFullInstanceTypes(
      (e) => e === ShapeType.CIRCLE,
    )[0];

    if (!circleInstanceData) return;

    const circleManager = circleInstanceData[1].manager;

    const currentTime = getServerTime();
    const finishedShardIds: number[] = [];

    animatingShardsRef.current.forEach((shard) => {
      const shardObject = circleManager.objects[shard.id];

      if (!shardObject) {
        finishedShardIds.push(shard.id);
        return;
      }

      const msSinceCollected = currentTime - shard.collectedAt;

      if (msSinceCollected >= ANIMATION_DURATION_MS) {
        circleManager.setMatrix(shard.id, hideMatrix);
        finishedShardIds.push(shard.id);
        return;
      }

      const progress = msSinceCollected / ANIMATION_DURATION_MS;

      shardOriginalPos.setFromMatrixPosition(shardObject.matrix);

      playerPosVec.set(
        shard.player.currentPosition.x,
        shard.player.currentPosition.y,
        shardOriginalPos.z,
      );

      translateToPlayer.makeTranslation(playerPosVec).invert();

      const scale = 1.0 - progress * 0.1;
      scaleMatrix.makeScale(scale, scale, scale);

      translateBackToShard.makeTranslation(playerPosVec);

      finalTransform
        .copy(shardObject.matrix)
        .premultiply(translateToPlayer)
        .premultiply(scaleMatrix)
        .premultiply(translateBackToShard);

      circleManager.setMatrix(shard.id, finalTransform);
    });

    for (const id of finishedShardIds) {
      animatingShardsRef.current.delete(id);
    }
  });

  return null;
};

export default ShardCollectedAnimation;
